import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { FiClock } from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import { listConsultingBookings } from "../api/mintSpaceConsulting";

function formatCountdown(startAt) {
  const start = DateTime.fromISO(startAt);
  const now = DateTime.now();
  const diff = start.diff(now, ["days", "hours", "minutes", "seconds"]).toObject();
  if (start <= now) return null;
  const d = Math.floor(diff.days || 0);
  const h = Math.floor(diff.hours || 0);
  const m = Math.floor(diff.minutes || 0);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function ConsultingUpcomingBanner({ isDark, mintSpaceId, appInstanceId, onOpenSession }) {
  const { t } = useTranslation();
  const [next, setNext] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!mintSpaceId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await listConsultingBookings(mintSpaceId, { instanceId: appInstanceId });
        if (!res.success || cancelled) return;
        const upcoming = (res.data.bookings || []).find(
          (b) => b.status === "scheduled" && DateTime.fromISO(b.startAt) > DateTime.now()
        );
        setNext(upcoming || null);
      } catch {
        if (!cancelled) setNext(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mintSpaceId, appInstanceId, tick]);

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  if (!next) return null;

  const countdown = formatCountdown(next.startAt);
  const base = isDark ? "text-app-consulting-soft" : "text-app-consulting-soft";
  const wrap = isDark
    ? "border-transparent bg-app-consulting-deep"
    : "border-transparent bg-app-consulting-deep";

  return (
    <div className={`mx-3 mt-2 flex flex-wrap items-center gap-2 rounded-radius-sm border px-3 py-2.5 ${wrap}`}>
      <FiClock className="h-4 w-4 shrink-0 text-app-consulting-soft" />
      <div className="min-w-0 flex-1">
        <p className={`text-footnote-semibold uppercase tracking-wide ${base}`}>
          {t("consulting.upcomingTitle") || "Upcoming consultation"}
        </p>
        <p className={`text-subheadline-semibold ${base}`}>{next.label}</p>
        {countdown && (
          <p className={`text-caption-1 opacity-80 ${base}`}>
            {t("consulting.startsIn") || "Starts in"} {countdown}
          </p>
        )}
      </div>
      {next.engagement && (
        <button
          type="button"
          onClick={() => onOpenSession?.(next.engagement)}
          className="shrink-0 rounded-radius-xs bg-app-consulting-deep px-2.5 py-1.5 text-caption-1-semibold text-white"
        >
          {t("consulting.openSession") || "Open session"}
        </button>
      )}
    </div>
  );
}
